// Qué hacer con lo que responde POST /subscriptions/checkout, tanto al CREAR el
// pago como al CONFIRMARLO. Lógica pura, compartida por /pay y /subscribe: las
// dos pantallas tenían la misma decisión escrita dos veces, y las dos mal (ver
// confirmVerdict).

import type { CheckoutSession, ConfirmCheckoutResult } from '@/lib/api';

// ---------------------------------------------------------------------------
// Textos compartidos por las dos pantallas.
// ---------------------------------------------------------------------------

/** Aviso del paso de pago cuando la pasarela rechazó el cobro. */
export const PAYMENT_REJECTED_MESSAGE =
  'El pago fue rechazado y no se hizo ningún cobro. Podés intentar de nuevo con la misma u otra tarjeta.';

/** La frase que evita el pago doble mientras el cobro está sin confirmar. */
export const DO_NOT_REPAY_MESSAGE =
  'No vuelvas a pagar: si el cobro se hizo, lo acreditamos automáticamente en unos minutos.';

// ---------------------------------------------------------------------------
// Confirmación
// ---------------------------------------------------------------------------

export type ConfirmVerdict = 'activated' | 'rejected' | 'pending';

/**
 * Veredicto de una respuesta del confirm.
 *
 * El rechazo se mira PRIMERO: en un cambio de plan la suscripción sigue viva,
 * así que el backend responde `status: 'active'` junto con
 * `payment_status: 'rejected'`. Mirar `status` antes daría por bueno un pago
 * que la pasarela rechazó.
 */
export function confirmVerdict(res: {
  status: string;
  activated: boolean;
  payment_status?: string;
}): ConfirmVerdict {
  if (res.payment_status === 'rejected') return 'rejected';
  if (res.activated || res.status === 'active') return 'activated';
  return 'pending';
}

/** Sondeo normal tras el aviso de éxito del widget (~17 s en total). */
export const CONFIRM_DELAYS: readonly number[] = [0, 1500, 3000, 5000, 8000];
/** Pasarela con redirect: el primer cobro puede tardar más en aprobarse. */
export const GATEWAY_CONFIRM_DELAYS: readonly number[] = [0, 2000, 4000, 8000, 15000];
/**
 * Al retomar un pago guardado no sabemos si el usuario llegó a pagar: sondeo
 * corto y enseguida la pantalla con las opciones, en vez de 17 s de espera.
 */
export const RESUME_CONFIRM_DELAYS: readonly number[] = [0, 1500, 3000];
/** Una sola consulta (antes de reiniciar, o el chequeo de fondo). */
export const SINGLE_CONFIRM: readonly number[] = [0];

export type PollResult<T> =
  | { kind: 'activated'; res: T }
  | { kind: 'rejected'; res: T }
  | { kind: 'pending' }
  | { kind: 'unauthorized' };

function isUnauthorized(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { status?: unknown }).status === 401;
}

const realSleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * Sondea el confirm hasta un veredicto definitivo o hasta agotar `delays`.
 * Corta apenas hay activación O rechazo (antes el rechazo se ignoraba y el
 * sondeo seguía hasta decir "el pago se registró" sobre un pago rechazado).
 * Un error que no sea 401 cuenta como "todavía no sabemos" y se reintenta.
 */
export async function pollConfirm<T extends { status: string; activated: boolean; payment_status?: string }>(
  confirm: () => Promise<T>,
  delays: readonly number[],
  sleep: (ms: number) => Promise<void> = realSleep,
): Promise<PollResult<T>> {
  for (const delay of delays) {
    if (delay) await sleep(delay);
    try {
      const res = await confirm();
      const verdict = confirmVerdict(res);
      if (verdict === 'activated') return { kind: 'activated', res };
      if (verdict === 'rejected') return { kind: 'rejected', res };
    } catch (e) {
      if (isUnauthorized(e)) return { kind: 'unauthorized' };
    }
  }
  return { kind: 'pending' };
}

// ---------------------------------------------------------------------------
// Creación
// ---------------------------------------------------------------------------

export type CheckoutOutcome =
  // El backend encontró un pago anterior ya aprobado: no hay nada que pagar.
  | { kind: 'activated'; res: Pick<ConfirmCheckoutResult, 'redirect_to' | 'redirect_uri_ok'> }
  // Pasarela con redirect (MercadoPago): se paga en su sitio.
  | { kind: 'redirect'; url: string }
  // Line: widget embebido.
  | { kind: 'widget'; checkoutUrl: string; paymentId: string }
  // Respuesta sin activación ni datos para pagar: nunca montar el widget.
  | { kind: 'invalid' };

/**
 * Qué hacer con la respuesta de la CREACIÓN del checkout. La activación se mira
 * primero: esa respuesta no trae `checkout_url`, y montar el widget con una URL
 * vacía le pediría al usuario que pague algo que ya pagó.
 */
export function checkoutOutcome(
  session: CheckoutSession,
  redirectProviders: ReadonlySet<string>,
): CheckoutOutcome {
  const s = session as Partial<{
    checkout_url: string;
    payment_id: string;
    provider: string;
    status: string;
    activated: boolean;
    redirect_to: string;
    redirect_uri_ok: boolean;
  }>;
  if (s.activated === true || (s.status === 'active' && !s.checkout_url)) {
    return {
      kind: 'activated',
      res: {
        ...(s.redirect_to ? { redirect_to: s.redirect_to } : {}),
        ...(s.redirect_uri_ok === undefined ? {} : { redirect_uri_ok: s.redirect_uri_ok }),
      },
    };
  }
  if (!s.checkout_url) return { kind: 'invalid' };
  if (s.provider && redirectProviders.has(s.provider)) {
    return { kind: 'redirect', url: s.checkout_url };
  }
  if (!s.payment_id) return { kind: 'invalid' };
  return { kind: 'widget', checkoutUrl: s.checkout_url, paymentId: s.payment_id };
}
